import { create } from 'zustand';
import type { NotificationSeverityName } from "@/lib/tokens";
import {
  formatNotificationCopy,
  normalizeNotificationCopy,
  type NotificationCopyFields,
  type NotificationFormattedCopy,
} from "@/lib/notificationCopy";
import type { Bot, KPI, ActivityFeed, NotificationCount } from '../types';
import { api, automationAPI, kpiAPI } from '../services/api';
import {
  acknowledgeAllNotifications,
  acknowledgeNotifications,
  fetchNotifications as fetchNotificationData,
  resolveTenantId,
  type StoredNotification,
} from '@/services/notifications';
import { resolveApiUrl } from '@/lib/apiBase';
import { isPageExitFetchAbort, isPageExiting } from '@/lib/pageLifecycle';

interface ApiState {
  isConnected: boolean;
  lastUpdate: Date | null;
  connectionStatus: string;
  connectionClass: string;
  checkConnection: () => Promise<void>;
}

export const useApiStore = create<ApiState>((set, get) => ({
  isConnected: false,
  lastUpdate: null,
  connectionStatus: 'disconnected',
  connectionClass: 'status-error',

  checkConnection: async () => {
    try {
      await api.get('/health');
      set({
        isConnected: true,
        lastUpdate: new Date(),
        connectionStatus: 'connected',
        connectionClass: 'status-online'
      });
    } catch (error) {
      set({
        isConnected: false,
        connectionStatus: 'disconnected',
        connectionClass: 'status-error'
      });
      console.error('API connection failed:', error);
    }
  }
}));

interface BotsState {
  bots: Bot[];
  isLoading: boolean;
  error: string | null;
  activeBots: Bot[];
  botCount: number;
  activeBotCount: number;
  fetchBots: () => Promise<void>;
  startBot: (botId: string) => Promise<void>;
  stopBot: (botId: string) => Promise<void>;
  createBot: (botData: Partial<Bot>) => Promise<void>;
}

export const useBotsStore = create<BotsState>((set, get) => ({
  bots: [],
  isLoading: false,
  error: null,
  activeBots: [],
  botCount: 0,
  activeBotCount: 0,

  fetchBots: async () => {
    set({ isLoading: true, error: null });
    try {
      const rawBots = await automationAPI.getAll();
      const botRows = Array.isArray(rawBots) ? rawBots : [];
      const bots = botRows.map((bot: any) => {
        const botUuid =
          bot.botUuid ??
          bot.bot_uuid ??
          bot.uuid ??
          (bot.id != null ? String(bot.id) : `bot-${Math.random().toString(36).slice(2)}`);
        return {
          ...bot,
          botUuid,
          legacyId: bot.id ?? bot.legacyId ?? null,
          id: botUuid,
        };
      });
      const activeBots = bots.filter((bot: Bot) => bot.status === 'running');

      set({
        bots,
        activeBots,
        botCount: bots.length,
        activeBotCount: activeBots.length,
        isLoading: false
      });
    } catch (err) {
      set({ error: 'Failed to fetch bots', isLoading: false });
      console.error('Error fetching bots:', err);
    }
  },

  startBot: async (botId: string) => {
    try {
      await automationAPI.start(botId);
      await get().fetchBots(); // Refresh data
    } catch (err) {
      set({ error: 'Failed to start bot' });
      console.error('Error starting bot:', err);
    }
  },

  stopBot: async (botId: string) => {
    try {
      await automationAPI.stop(botId);
      await get().fetchBots(); // Refresh data
    } catch (err) {
      set({ error: 'Failed to stop bot' });
      console.error('Error stopping bot:', err);
    }
  },

  createBot: async (botData: Partial<Bot>) => {
    try {
      await api.post('/bots', botData);
      await get().fetchBots(); // Refresh data
    } catch (err) {
      set({ error: 'Failed to create bot' });
      console.error('Error creating bot:', err);
      throw err;
    }
  }
}));

interface KPIState {
  kpis: KPI[];
  isLoading: boolean;
  fetchKPIs: () => Promise<void>;
}

export const useKPIStore = create<KPIState>((set) => ({
  kpis: [],
  isLoading: false,

  fetchKPIs: async () => {
    set({ isLoading: true });
    try {
      const data = await kpiAPI.list({ includeHistory: true, historyLimit: 60 });
      set({ kpis: data, isLoading: false });
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      set({ isLoading: false });
    }
  }
}));

interface ActivityState {
  activities: ActivityFeed[];
  isLoading: boolean;
  fetchActivities: () => Promise<void>;
  addActivity: (activity: ActivityFeed) => void;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  activities: [],
  isLoading: false,

  fetchActivities: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get('/activities');
      set({ activities: response.data, isLoading: false });
    } catch (err) {
      console.error('Error fetching activities:', err);
      set({ isLoading: false });
    }
  },

  addActivity: (activity: ActivityFeed) => {
    const { activities } = get();
    const newActivities = [activity, ...activities];
    // Keep only last 50 activities
    if (newActivities.length > 50) {
      newActivities.splice(50);
    }
    set({ activities: newActivities });
  }
}));

export interface NotificationEntry {
  id: string;
  type: NotificationSeverityName;
  copy: NotificationCopyFields;
  formatted: NotificationFormattedCopy;
  title: string;
  message: string;
  meta?: string;
  timestamp: string;
  read: boolean;
  category: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
  referenceId?: string | null;
}

interface NotificationState {
  count: number;
  notifications: NotificationEntry[];
  severityCounts: Record<NotificationSeverityName, number>;
  totalCounts: Record<NotificationSeverityName, number>;
  loading: boolean;
  error?: string;
  lastSynced?: string;
  fetchNotifications: (tenantId?: string) => Promise<void>;
  incrementCount: () => void;
  resetCount: () => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  initLiveUpdates: () => void;
  stopLiveUpdates: () => void;
}

const MAX_NOTIFICATION_ENTRIES = 100;

const createEmptySeverityMap = (): Record<NotificationSeverityName, number> => ({
  info: 0,
  success: 0,
  warning: 0,
  error: 0,
  critical: 0,
});

const computeSeverityTallies = (notifications: NotificationEntry[]) => {
  const unreadBySeverity = createEmptySeverityMap();
  const totalBySeverity = createEmptySeverityMap();
  let unreadTotal = 0;

  notifications.forEach((notification) => {
    const severity = notification.type;
    if (totalBySeverity[severity] !== undefined) {
      totalBySeverity[severity] += 1;
    }
    if (!notification.read && unreadBySeverity[severity] !== undefined) {
      unreadBySeverity[severity] += 1;
      unreadTotal += 1;
    }
  });

  return {
    unreadBySeverity,
    totalBySeverity,
    unreadTotal,
  };
};


const deriveCopyFields = (
  source: {
    copy?: StoredNotification['copy'];
    title?: string;
    message?: string;
    next_step?: string | null;
    nextStep?: string | null;
  },
): NotificationCopyFields => {
  const copyPayload = source.copy ?? undefined;
  const nextStepCandidate =
    copyPayload?.next_step ??
    copyPayload?.nextStep ??
    source.next_step ??
    source.nextStep;
  return normalizeNotificationCopy({
    event: copyPayload?.event ?? source.title,
    impact: copyPayload?.impact ?? source.message,
    nextStep: nextStepCandidate,
  });
};

const buildFormattedNotification = (
  item: StoredNotification,
): { copy: NotificationCopyFields; formatted: NotificationFormattedCopy } => {
  const copy = deriveCopyFields(item);
  const formatted = formatNotificationCopy(copy, {
    timestamp: item.created_at,
    referenceId: item.reference_id ?? undefined,
  });
  return { copy, formatted };
};

const mapNotification = (item: StoredNotification): NotificationEntry => {
  const { copy, formatted } = buildFormattedNotification(item);
  return {
    id: item.id,
    type: item.severity,
    copy,
    formatted,
    title: formatted.title,
    message: formatted.body,
    meta: formatted.meta,
    timestamp: item.created_at,
    read: item.read,
    category: item.category,
    actionUrl: item.action_url ?? undefined,
    metadata: item.metadata ?? undefined,
    referenceId: item.reference_id ?? undefined,
  };
};

const mapEventToNotification = (payload: any): NotificationEntry => {
  const createdAt = typeof payload?.created_at === 'string' ? payload.created_at : new Date().toISOString();
  const copy = deriveCopyFields({
    copy: payload?.copy,
    title: payload?.title,
    message: payload?.message,
    next_step: payload?.next_step,
    nextStep: payload?.nextStep,
  });
  const formatted = formatNotificationCopy(copy, {
    timestamp: createdAt,
    referenceId: typeof payload?.reference_id === 'string' ? payload.reference_id : undefined,
  });

  return {
    id: typeof payload?.id === 'string' ? payload.id : crypto.randomUUID(),
    type: (payload?.severity as NotificationSeverityName) || 'info',
    copy,
    formatted,
    title: formatted.title,
    message: formatted.body,
    meta: formatted.meta,
    timestamp: createdAt,
    read: Boolean(payload?.read),
    category: typeof payload?.category === 'string' ? payload.category : 'system',
    actionUrl: payload?.action_url ?? undefined,
    metadata: payload?.metadata ?? undefined,
    referenceId: typeof payload?.reference_id === 'string' ? payload.reference_id : undefined,
  };
};

let notificationsEventSource: EventSource | null = null;
let notificationsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let notificationsTenantId: string | null = null;
const closedNotificationSources = new WeakSet<EventSource>();

const closeNotificationSource = (source: EventSource) => {
  closedNotificationSources.add(source);
  source.close();
};

export const useNotificationStore = create<NotificationState>((set, get) => ({
  count: 0,
  notifications: [],
  severityCounts: createEmptySeverityMap(),
  totalCounts: createEmptySeverityMap(),
  loading: false,
  error: undefined,
  lastSynced: undefined,

  fetchNotifications: async (tenantId?: string) => {
    set({ loading: true, error: undefined });
    try {
      const raw = await fetchNotificationData(tenantId);
      const notifications = raw.map(mapNotification);
      const { unreadBySeverity, totalBySeverity, unreadTotal } = computeSeverityTallies(notifications);
      set({
        notifications,
        count: unreadTotal,
        severityCounts: unreadBySeverity,
        totalCounts: totalBySeverity,
        loading: false,
        error: undefined,
        lastSynced: new Date().toISOString(),
      });
      get().initLiveUpdates();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load notifications';
      if (message.startsWith('404')) {
        set({
          notifications: [],
          count: 0,
          severityCounts: createEmptySeverityMap(),
          totalCounts: createEmptySeverityMap(),
          loading: false,
          error: undefined,
          lastSynced: new Date().toISOString(),
        });
        return;
      }
      if (isPageExitFetchAbort(err)) {
        set({ loading: false });
        return;
      }
      console.error('Failed to fetch notifications', err);
      set({ loading: false, error: message });
    }
  },

  incrementCount: () => set((state) => {
    const tallies = computeSeverityTallies(state.notifications);
    return {
      count: tallies.unreadTotal,
      severityCounts: tallies.unreadBySeverity,
      totalCounts: tallies.totalBySeverity,
    };
  }),
  resetCount: () => set((state) => {
    const tallies = computeSeverityTallies(state.notifications);
    return {
      count: tallies.unreadTotal,
      severityCounts: tallies.unreadBySeverity,
      totalCounts: tallies.totalBySeverity,
    };
  }),

  markAsRead: async (id: string) => {
    try {
      await acknowledgeNotifications([id]);
      set((state) => {
        const notifications = state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        );
        const tallies = computeSeverityTallies(notifications);
        return {
          notifications,
          count: tallies.unreadTotal,
          severityCounts: tallies.unreadBySeverity,
          totalCounts: tallies.totalBySeverity,
        };
      });
    } catch (err) {
      console.error('Failed to acknowledge notification', err);
      const message = err instanceof Error ? err.message : 'Unable to acknowledge notification';
      set({ error: message });
    }
  },

  markAllAsRead: async () => {
    try {
      await acknowledgeAllNotifications();
      set((state) => {
        const notifications = state.notifications.map((n) => ({ ...n, read: true }));
        const tallies = computeSeverityTallies(notifications);
        return {
          notifications,
          count: tallies.unreadTotal,
          severityCounts: tallies.unreadBySeverity,
          totalCounts: tallies.totalBySeverity,
        };
      });
    } catch (err) {
      console.error('Failed to acknowledge notifications', err);
      const message = err instanceof Error ? err.message : 'Unable to acknowledge notifications';
      set({ error: message });
    }
  },

  initLiveUpdates: () => {
    if (import.meta.env.VITE_DEMO_MODE === '1') {
      return;
    }
    if (notificationsEventSource) {
      return;
    }

    const tenantId = resolveTenantId();
    if (!tenantId) {
      return;
    }
    notificationsTenantId = tenantId;

    const openStream = () => {
      if (!notificationsTenantId) return;
      if (notificationsEventSource) return;

      const streamUrl = resolveApiUrl(
        `/api/stream/notifications?tenant_id=${encodeURIComponent(notificationsTenantId)}`,
      );

      try {
        const source = new EventSource(streamUrl, { withCredentials: true });
        notificationsEventSource = source;

        source.addEventListener('notification', (event) => {
          try {
            const payload = JSON.parse((event as MessageEvent).data);
            const notification = mapEventToNotification(payload);
            set((state) => {
              const exists = state.notifications.some((n) => n.id === notification.id);
              const notifications = exists
                ? state.notifications
                : [notification, ...state.notifications].slice(0, MAX_NOTIFICATION_ENTRIES);
              const tallies = computeSeverityTallies(notifications);
              return {
                notifications,
                count: tallies.unreadTotal,
                severityCounts: tallies.unreadBySeverity,
                totalCounts: tallies.totalBySeverity,
              };
            });
          } catch (error) {
            console.error('Failed to process notification event', error);
          }
        });

        source.addEventListener('notification-snapshot', (event) => {
          try {
            const payload = JSON.parse((event as MessageEvent).data);
            const items: StoredNotification[] = Array.isArray(payload?.items) ? payload.items : [];
            const notifications = items.map(mapNotification).slice(0, MAX_NOTIFICATION_ENTRIES);
            const tallies = computeSeverityTallies(notifications);
            set({
              notifications,
              count: tallies.unreadTotal,
              severityCounts: tallies.unreadBySeverity,
              totalCounts: tallies.totalBySeverity,
              lastSynced: new Date().toISOString(),
            });
          } catch (error) {
            console.error('Failed to process notification snapshot', error);
          }
        });

        source.onerror = (error) => {
          if (closedNotificationSources.has(source) || isPageExiting()) {
            return;
          }
          console.error('Notification stream error', error);
          if (notificationsEventSource === source) {
            notificationsEventSource = null;
          }
          closeNotificationSource(source);
          if (!notificationsReconnectTimer) {
            notificationsReconnectTimer = setTimeout(() => {
              notificationsReconnectTimer = null;
              openStream();
            }, 5000);
          }
        };
      } catch (error) {
        console.error('Failed to open notification stream', error);
        if (!notificationsReconnectTimer) {
          notificationsReconnectTimer = setTimeout(() => {
            notificationsReconnectTimer = null;
            openStream();
          }, 5000);
        }
      }
    };

    openStream();
  },

  stopLiveUpdates: () => {
    if (notificationsReconnectTimer) {
      clearTimeout(notificationsReconnectTimer);
      notificationsReconnectTimer = null;
    }
    if (notificationsEventSource) {
      closeNotificationSource(notificationsEventSource);
      notificationsEventSource = null;
    }
  },
}));
